import { connect } from 'react-redux';
import { useCallback } from 'react';
import { isAfter } from 'date-fns';

import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { ResultDecisionsItemContentSignButton } from 'KnmErknmPackage/ErknmResultDecisionsModule/components/ErknmResultDecisionsItemPaperlesContent/components/ResultDecisionsItemContentSignButton/ResultDecisionsItemContentSignButton';
import { IDecisionFormRef } from 'DecisionPackage/MainModule/interfaces/IDecisionFormRef';
import { IDecisionResultDecisionForm } from 'DecisionPackage/MainModule/interfaces/IDecisionResultDecisionForm';
import { ResultDecisionsItemContentExtract } from 'KnmErknmPackage/ErknmResultDecisionsModule/components/ErknmResultDecisionsItemPaperlesContent/components/ResultDecisionsItemContentExtract/ResultDecisionsItemContentExtract';
import { ResultDecisionsItemContentGeneral } from 'KnmErknmPackage/ErknmResultDecisionsModule/components/ErknmResultDecisionsItemPaperlesContent/components/ResultDecisionsItemContentGeneral/ResultDecisionsItemContentGeneral';
import { IDecisionOrganizationForm } from 'DecisionPackage/MainModule/interfaces/IDecisionOrganizationForm';
import { IKnmErknmFormViolatedRequirementsNpa } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormViolatedRequirementsNpa';
import { ResultDecisionsItemContentSignInfo } from 'KnmErknmPackage/ErknmResultDecisionsModule/components/ErknmResultDecisionsItemPaperlesContent/components/ResultDecisionsItemContentSignInfo/ResultDecisionsItemContentSignInfo';
import { ResultDecisionsItemContentIntroductions } from 'KnmErknmPackage/ErknmResultDecisionsModule/components/ErknmResultDecisionsItemPaperlesContent/components/ResultDecisionsItemContentIntroductions/ResultDecisionsItemContentIntroductions';
import { getDecisionOnSaveButtonClick } from 'DecisionPackage/MainModule/selectors/decisionSelectors';
import { IDecisionForm } from 'DecisionPackage/MainModule/interfaces/IDecisionForm';
import { parseDate } from 'CorePackage/MainModule/common/formatDate';
import { DATE_FORMAT_LONG_FNS } from 'CorePackage/MainModule/constants/dateFormats';
import { ResultDecisionsItemContentCancelInfo } from 'KnmErknmPackage/ErknmResultDecisionsModule/components/ErknmResultDecisionsItemPaperlesContent/components/ResultDecisionsItemContentCancelInfo/ResultDecisionsItemContentCancelInfo';

import { DecisionResultItemContentRequirements } from './DecisionResultItemContentRequirements/DecisionResultItemContentRequirements';
import { DecisionResultItemContentActs } from './DecisionResultItemContentActs/DecisionResultItemContentActs';
import styles from './DecisionResultItemPaperless.module.scss';
import { DecisionResultItemContentSubject } from './DecisionResultItemContentSubject';

interface IDecisionResultItemPaperlessProps {
  formRef: IDecisionFormRef | undefined;
  isWatchOnly: boolean;
  value: IDecisionResultDecisionForm;
  name: string;
  organization: IDecisionOrganizationForm | undefined;
  violatedRequirementsNpa: IKnmErknmFormViolatedRequirementsNpa[] | undefined;
  isPaperless: boolean;
  resultDecisionId: string;
  resultDecisionIndex: number;
  isLater4111ForDecision?: boolean;
  isExpandAllInPrescription?: boolean;
}

interface IDecisionResultItemPaperlessStoreProps {
  onSaveButtonClick: (() => void) | undefined;
}

const DecisionResultItemPaperlessWithoutStore = ({
  onSaveButtonClick,
  formRef,
  isWatchOnly,
  value,
  name,
  organization,
  violatedRequirementsNpa,
  isPaperless,
  resultDecisionId,
  resultDecisionIndex,
  isLater4111ForDecision,
  isExpandAllInPrescription,
}: IDecisionResultItemPaperlessProps & IDecisionResultItemPaperlessStoreProps) => {
  const violatedMandatoryRequirements = formRef?.current?.getFieldState(
    `${name}.eliminatedMandatoryRequirements` as keyof IDecisionForm,
  )?.value as IKnmErknmFormViolatedRequirementsNpa[] | undefined;
  const isDisabledField = isWatchOnly || !!value.document;
  const isDisabledButton = isWatchOnly;

  const onChangeFixPlanDate = useCallback(() => {
    let maxDate = '';

    violatedMandatoryRequirements?.forEach(vmr => {
      vmr.violatedMandatory?.forEach(vm => {
        vm.violations?.forEach(v => {
          if (v.fixPlanDates?.length && v.fixPlanDates[0].value) {
            const currentDate = parseDate({
              date: v.fixPlanDates[0].value,
              parseFormat: DATE_FORMAT_LONG_FNS,
            });
            const maxDateFormated = parseDate({ date: maxDate || '', parseFormat: DATE_FORMAT_LONG_FNS });

            if (!maxDate || isAfter(currentDate || 0, maxDateFormated || 0)) {
              maxDate = v.fixPlanDates[0].value;
            }
          }
        });
      });
    });

    if (maxDate) {
      formRef?.current?.change(`${name}.injunction.dateResolved` as keyof IDecisionForm, maxDate);
    }
  }, [formRef, violatedMandatoryRequirements]);

  return (
    <div className={styles.Wrapper}>
      <DecisionResultItemContentActs resultDecisionId={resultDecisionId} organization={organization} />
      <ResultDecisionsItemContentExtract extract={value.document} />
      <ResultDecisionsItemContentGeneral
        formRef={formRef}
        isDecision
        isDisabledField={isDisabledField}
        dateName={`${name}.date`}
        dateResolvedName={`${name}.injunction.dateResolved`}
        infoResolvedName={`${name}.injunction.note`}
        infoResolvedId={`organization.${resultDecisionId}.injunction.note`}
        isHideInfoResolved={organization?.act?.isViolatedMandatoryRequirements?.id === 1}
        name={name}
        agreementStatus={value.agreementStatus}
      />
      <DecisionResultItemContentSubject
        organization={organization}
        isLater4111ForDecision={isLater4111ForDecision}
        name={name}
        value={value}
        formRef={formRef}
        isWatchOnly={isDisabledField}
        resultDecisionId={resultDecisionId}
      />
      <DecisionResultItemContentRequirements
        formRef={formRef}
        watchOnly={isDisabledField}
        isViolationFieldsWatchOnly={isWatchOnly}
        requirementsNpa={violatedRequirementsNpa}
        requirementEliminationsFieldName={`${name}.eliminatedMandatoryRequirements`}
        isPaperless={isPaperless}
        onChangeFixPlanDate={onChangeFixPlanDate}
        id={`organization.${resultDecisionId}.eliminatedMandatoryRequirements`}
        signed={!!value.signed}
        isExpandAllInPrescription={isExpandAllInPrescription}
        extractVersion={value.extractVersion}
      />
      <ResultDecisionsItemContentSignInfo
        isDisabledField={isDisabledField}
        hasResultDecisionFirstExtractDateTime={!!value?.firstExtractDateTime}
        fioSignerFieldName={`${name}.fioSigner`}
        titleSignerFieldName={`${name}.titleSigner`}
        fioSignerId={`organization.${resultDecisionId}.fioSigner`}
        titleSignerId={`organization.${resultDecisionId}.titleSigner`}
        isDecision
        isNew={value?.isNew === true}
        name={name}
        kindCode={value?.kind?.code}
        formRef={formRef}
        isSigned={value?.signed === true}
        resultDecisionId={`organization.${resultDecisionId}`}
        resultDecisionKindCode={value?.kind?.code}
      />
      {value.document && (
        <ResultDecisionsItemContentIntroductions
          id={`organization.${resultDecisionId}`}
          name={name}
          formRef={formRef}
          isDecision
        />
      )}
      <ResultDecisionsItemContentSignButton
        isDisabledField={isDisabledButton}
        value={value}
        formRef={formRef}
        onSaveButtonClick={onSaveButtonClick}
        isDecision
        isResultDecision
        decisionIndex={resultDecisionIndex}
        name={name}
      />
      <ResultDecisionsItemContentCancelInfo
        cancelDocument={value.cancelDocument}
        cancelCommentFieldName={`${name}.cancelComment`}
        fioSignerFieldName={`${name}.cancelFioSigner`}
        titleSignerFieldName={`${name}.cancelTitleSigner`}
        value={value}
        isPm
        isDecision={true}
        isCancelSigned={value.isCancelSigned}
      />
    </div>
  );
};

const mapStateToProps = (state: IRootState): IDecisionResultItemPaperlessStoreProps => ({
  onSaveButtonClick: getDecisionOnSaveButtonClick(state),
});

export const DecisionResultItemPaperless = connect(mapStateToProps)(DecisionResultItemPaperlessWithoutStore);
